import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  Users, 
  Target, 
  Rocket, 
  Zap,
  Globe,
  Heart
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const values = [
  {
    title: "Security by Design",
    desc: "We believe your project data and credentials deserve the highest level of protection. Security isn't an afterthought; it's our foundation.",
    icon: ShieldCheck,
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    title: "Intelligence First",
    desc: "Leveraging cutting-edge AI to automate the mundane, allowing your team to focus on high-impact creative work.",
    icon: Sparkles,
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  },
  {
    title: "Radical Transparency",
    desc: "Real-time updates and detailed activity logs ensure everyone is aligned and accountable, every step of the way.",
    icon: Users,
    color: "bg-green-50",
    iconColor: "text-green-600"
  },
  {
    title: "Relentless Innovation",
    desc: "We are constantly evolving TeamBoard to meet the changing needs of modern engineering and design teams.",
    icon: Rocket,
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  }
];

const AboutUsPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-[80vh] flex items-center overflow-hidden pt-20">
          <div className="absolute -top-20 right-[-100px] w-80 h-80 rounded-[90px] bg-blue-100/50" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-[140px] bg-[#222875]/5" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
            <div className="reveal">
              <span className="section-eyebrow">Our Mission</span>
              <h1 className="mt-6 mb-8 text-navy">
                Empowering teams to <span className="text-blue-600">build the future,</span> together.
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl">
                PMS was born from a simple realization: project management tools should work for you, not the other way around. We've built a workspace that combines intuitive design with powerful intelligence.
              </p>
              <div className="flex justify-start gap-4">
                <Link to="/contact" className="btn-primary">
                  Join Our Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#story" onClick={(e) => handleNavClick(e, "story")} className="btn-secondary">
                  Read Our Story
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section id="story" className="shell-white">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">The Story Behind PMS</span>
                <h2 className="mt-4 mb-6 text-navy">Born from a need for <span className="text-blue-600">clarity and security.</span></h2>
                <div className="space-y-4 text-lg text-gray-600">
                  <p>
                    In a world of fragmented tools and constant notifications, we saw teams struggling to keep track of what truly matters. Important credentials were lost in chats, task context was buried in long email threads, and project specs were manual chores to break down.
                  </p>
                  <p>
                    We built PMS to be the "source of truth" for modern teams. A place where Kanban efficiency meets a secure vault for project secrets, all enhanced by AI that understands your documents as well as you do.
                  </p>
                </div>
              </div>
              <div className="relative reveal-right">
                <div className="bg-navy rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Target className="w-32 h-32 text-white" />
                  </div>
                  <div className="relative z-10">
                    <h3 className="text-3xl font-bold mb-6 text-white">Our Vision</h3>
                    <p className="text-xl text-blue-100 leading-relaxed mb-8">
                      "To become the most trusted and intelligent workspace for engineering and design teams worldwide, fostering innovation through seamless collaboration."
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-sky flex items-center justify-center">
                        <Heart className="w-6 h-6 text-navy" />
                      </div>
                      <div>
                        <p className="font-bold text-white">Built with Passion</p>
                        <p className="text-sm text-blue-200">For teams, by teams</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section id="values" className="shell-sky">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="section-eyebrow">Our Core Values</span>
              <h2 className="mt-4 text-navy">The principles that <span className="text-blue-600">guide us.</span></h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, idx) => (
                <div key={idx} className="card-marketing card-hover bg-white reveal stagger-1">
                  <div className={`w-14 h-14 ${value.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <value.icon className={`w-7 h-7 ${value.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why We're Different */}
        <section className="shell-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 reveal">
              <span className="section-eyebrow">The PMS Difference</span>
              <h2 className="mt-4 mb-6 text-navy">Why teams choose us over <span className="text-blue-600">traditional tools.</span></h2>
            </div>
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="flex flex-col items-center text-center p-8 reveal stagger-1">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white mb-6 shadow-lg shadow-blue-200">
                  <Zap className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Speed & Agility</h3>
                <p className="text-gray-600">
                  Real-time synchronization ensures that your team is always working on the latest data, without refresh delays.
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-8 reveal stagger-2">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center text-white mb-6 shadow-lg shadow-gray-200">
                  <ShieldCheck className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Enterprise Security</h3>
                <p className="text-gray-600">
                  Role-based permissions and encrypted credential storage give you complete control over your project assets.
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-8 reveal stagger-3">
                <div className="w-16 h-16 bg-sky rounded-full flex items-center justify-center text-navy mb-6 shadow-lg shadow-blue-100">
                  <Globe className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-navy">Global Collaboration</h3>
                <p className="text-gray-600">
                  Built for remote-first teams, PMS bridges the gap between time zones with intelligent notifications.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-white mb-8">Ready to experience the future of project management?</h2>
            <p className="text-xl text-blue-100 mb-12">
              Join the hundreds of teams already scaling their productivity with PMS.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Request a Demo
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default AboutUsPage;
